import { useState, useEffect, useCallback } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import Modal from "../Modals/Modal";
import withAlert from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import Icon from "../Utils/Icon";
import AuthenticationTwoFactorManager from "./AuthenticationTwoFactorManager";
import getQueryStringParamByName from "../../helpers/getQueryStringParamByName";

import { unlockAccount } from "../../mutations/UnlockAccountMutation";
import { useFormatMessage } from "../Intl/Context";

const AccountUnlocking = props => {
  const [isSubmitting, setIsSubmitting] = useState(true);
  const [didSuccess, setDidSuccess] = useState(false);
  const [didFail, setDidFail] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState(null);

  const t = useFormatMessage();
  const title = t(intlMessages.title);
  const submitting = t(intlMessages.submitting);
  const successMessage = t(intlMessages.successMessage);
  const failureMessage = t(intlMessages.failureMessage);
  const successAlertMessage = t(intlMessages.successAlertMessage);

  const modalClassName = classNames({
    "modal-sm": true,
    "modal-loader": isSubmitting
  });

  const loadingSpinner = isSubmitting ? (
    <div className="loading-spinner-container modal-loading-spinner">
      <Icon className="fa fa-circle-notch fa-spin" />
    </div>
  ) : null;

  const handleSignedIn = useCallback(() => {
    window.location.href = "/";
  }, []);

  const handleResponse = response => {
    setIsSubmitting(false);

    const result = response.unlockAccount;

    if (result.twoFactorStep) {
      // The account is unlocked but the platform requires SMS two-factor before sign-in:
      // hand off to the same challenge/enrollment flow used on the sign-in screen.
      setTwoFactorStep(result.twoFactorStep);
    } else if (result.success) {
      setDidSuccess(true);

      props.showAlert({ message: successAlertMessage, type: ALERT_TYPE_SUCCESS });

      // Redirect to home page and reload since the user has been automatically
      // signed in on account unlock.
      setTimeout(() => (window.location.href = "/"), 3000);
    } else {
      setDidFail(true);

      props.showAlert({ message: response.unlockAccount.notice, type: ALERT_TYPE_ERROR });
    }
  };

  useEffect(() => {
    unlockAccount(
      { unlockToken: getQueryStringParamByName("unlock_token") },
      handleResponse
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Define the displayed message in the modal depending on the current unlocking request
  // state
  let message;
  if (didSuccess) message = successMessage;
  else if (didFail) message = failureMessage;
  else message = submitting;

  return (
    <Modal shouldCloseOnOverlayClick={false} title={title} className={modalClassName}>
      {twoFactorStep ? (
        <AuthenticationTwoFactorManager
          twoFactorStep={twoFactorStep}
          onSignIn={handleSignedIn}
        />
      ) : (
        <div className="box margin-bottom-xs padding-x-lg padding-y-md">
          <p className="help-block" dangerouslySetInnerHTML={{ __html: message }} />

          {loadingSpinner}
        </div>
      )}
    </Modal>
  );
};

export default withAlert(AccountUnlocking);

const intlMessages = defineMessages({
  title: {
    id: "authentication.accountUnlocking.title",
    defaultMessage: "Déblocage de votre compte"
  },
  submitting: {
    id: "authentication.accountUnlocking.submitting",
    defaultMessage: `
      Votre compte va maintenant être débloqué à partir du code contenu
      dans le lien que vous venez d'utiliser. Merci de patienter ...
    `
  },
  successMessage: {
    id: "authentication.accountUnlocking.successMessage",
    defaultMessage: "Votre compte a bien été débloqué."
  },
  failureMessage: {
    id: "authentication.accountUnlocking.failureMessage",
    defaultMessage: `
      Votre compte n'a pu être débloqué, merci de vérifier que votre compte est bien
      bloqué et que vous avez utilisé un lien valide pour le déblocage. Si vous ne
      parvenez toujours pas à débloquer votre compte, vous pouvez contacter un
      administrateur de la plateforme.
    `
  },
  successAlertMessage: {
    id: "authentication.accountUnlocking.successAlertMessage",
    defaultMessage: `
      Votre compte a bien été débloqué, et vous avez été automatiquement connecté à votre
      compte. Vous allez être redirigé vers la page d'accueil dans quelques instants ...
    `
  }
});
